import { useCallback, useEffect, useRef, useState } from "react";
import { uploadEvaluationVideo } from "../api/evaluationVideoUploadApi";
import {
  createEvalMediaRecorder,
  enableEvalStreamAudio,
  ensureEvalStreamHasAudio,
  evalStreamHasLiveAudio,
  getEvalUserMedia,
  getLastEvalMicError,
  pickEvalVideoMimeType,
} from "../utils/evalVideoEncoding";
import {
  getHeldEvalCameraStream,
  holdEvalCameraStream,
  resumeHeldEvalCameraStream,
} from "../utils/evalCameraPermission";
import { EXERCISE_INSTRUCTION_SOURCE } from "./useCoachInstructionRecording";
import {
  appendCaptureQos,
  resolveCaptureQosRunId,
  setCaptureQosContext,
  startCaptureQosSampling,
  stopCaptureQosSampling,
} from "../utils/captureQos";
import { phoneCoversTable } from "../utils/tableRecordingTarget";
import { tablePhoneShareFields } from "../utils/tablePhoneCoverage";

function resolveTableCameraSource(payload) {
  const source = String(payload?.source || "")
    .trim()
    .toLowerCase();
  // coach_table_video is handled by useLiveTrainingEvalRecorder (same pipeline
  // as autoeval + debug log). This hook only records exercise instructions.
  if (source === EXERCISE_INSTRUCTION_SOURCE) return EXERCISE_INSTRUCTION_SOURCE;
  return null;
}

/**
 * Table-phone listener: when coach starts exercise-instruction capture for
 * this device's table, record A/V and upload as instruction media.
 */
export function useTableInstructionRecorder({
  socket,
  enabled = false,
  tableNumber = null,
  /** Every table a wide-angle table phone films (main included). */
  tableNumbers = null,
  cameraSide = "A",
  videoQuality = "low",
}) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTeId, setActiveTeId] = useState(null);

  const streamRef = useRef(null);
  const ownsStreamRef = useRef(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef(null);
  const captureMetaRef = useRef(null);
  const tableNumberRef = useRef(tableNumber);
  const tableNumbersRef = useRef(tableNumbers);
  const cameraSideRef = useRef(cameraSide);
  const qualityRef = useRef(videoQuality);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    tableNumberRef.current = tableNumber;
  }, [tableNumber]);
  useEffect(() => {
    tableNumbersRef.current = tableNumbers;
  }, [tableNumbers]);
  useEffect(() => {
    cameraSideRef.current = cameraSide;
  }, [cameraSide]);
  useEffect(() => {
    qualityRef.current = videoQuality;
  }, [videoQuality]);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const stopTracksIfOwned = useCallback(() => {
    if (!ownsStreamRef.current) {
      streamRef.current = null;
      return;
    }
    try {
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    streamRef.current = null;
    ownsStreamRef.current = false;
  }, []);

  const discardRecorder = useCallback(() => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.onstop = null;
        recorderRef.current.stop();
      }
    } catch {
      /* ignore */
    }
    recorderRef.current = null;
    chunksRef.current = [];
    mimeRef.current = null;
    captureMetaRef.current = null;
    stopTracksIfOwned();
    setRecording(false);
    setActiveTeId(null);
  }, [stopTracksIfOwned]);

  const startLocalRecording = useCallback(async (payload) => {
    if (recorderRef.current) return;
    setError(null);
    const teId = payload?.te_id != null ? String(payload.te_id) : null;
    if (!teId) return;

    let stream = getHeldEvalCameraStream();
    let owns = false;
    if (stream) {
      // A parked session stream records black frames and silence.
      resumeHeldEvalCameraStream();
      enableEvalStreamAudio(stream);
    } else {
      stream = await getEvalUserMedia({
        facingMode: "environment",
        includeAudio: true,
      });
      holdEvalCameraStream(stream);
      owns = true;
    }
    // Instruction clips are the coach's voice explanation — never start silent.
    // Wait for the mic (no short timeout); refuse if it never attaches.
    await ensureEvalStreamHasAudio(stream);
    enableEvalStreamAudio(stream);
    if (!evalStreamHasLiveAudio(stream)) {
      const reason = getLastEvalMicError() || "missing";
      throw new Error(
        reason === "NotAllowedError" || reason === "SecurityError"
          ? "Microphone blocked: instruction videos need sound. Allow the mic for this site."
          : "Microphone unavailable: instruction videos need sound."
      );
    }

    streamRef.current = stream;
    ownsStreamRef.current = owns;
    chunksRef.current = [];
    const mimeType = pickEvalVideoMimeType(undefined, { preferAudio: true });
    mimeRef.current = mimeType;
    const quality = qualityRef.current || "low";
    const recorder = createEvalMediaRecorder(stream, quality, mimeType);
    recorderRef.current = recorder;
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    captureMetaRef.current = {
      teId,
      captureId: payload?.capture_id || null,
      tableNumber: Number(payload?.table_number) || Number(tableNumberRef.current),
      startedAt: payload?.started_at || new Date().toISOString(),
      source: resolveTableCameraSource(payload) || EXERCISE_INSTRUCTION_SOURCE,
    };
    recorder.start(1000);
    setActiveTeId(teId);
    setRecording(true);
    setCaptureQosContext({
      flow: "instruction",
      session_id: teId,
      clip_id: captureMetaRef.current?.captureId || null,
      run_id: resolveCaptureQosRunId(),
    });
    appendCaptureQos("recording_start", {
      path: "mediarecorder",
      has_audio: true,
      mic_error: null,
    });
    startCaptureQosSampling();
  }, []);

  const stopAndUpload = useCallback(async () => {
    const meta = captureMetaRef.current;
    const rec = recorderRef.current;
    if (!rec || !meta?.teId) {
      discardRecorder();
      return null;
    }

    stopCaptureQosSampling();
    appendCaptureQos("recording_stop", {
      clip_id: meta.captureId || null,
      te_id: meta.teId,
    });
    setUploading(true);
    setError(null);
    try {
      const blob = await new Promise((resolve, reject) => {
        rec.onstop = () => {
          const type =
            mimeRef.current || chunksRef.current[0]?.type || "video/webm";
          resolve(new Blob(chunksRef.current, { type }));
        };
        try {
          if (rec.state !== "inactive") rec.stop();
          else {
            const type =
              mimeRef.current || chunksRef.current[0]?.type || "video/webm";
            resolve(new Blob(chunksRef.current, { type }));
          }
        } catch (err) {
          reject(err);
        }
      });

      recorderRef.current = null;
      chunksRef.current = [];
      stopTracksIfOwned();
      setRecording(false);

      if (!blob?.size) {
        setUploading(false);
        captureMetaRef.current = null;
        setActiveTeId(null);
        return null;
      }

      const side = String(cameraSideRef.current || "A")
        .trim()
        .toUpperCase();
      const clipKind = meta.source || EXERCISE_INSTRUCTION_SOURCE;
      const share = tablePhoneShareFields(
        meta.tableNumber,
        tableNumbersRef.current
      );
      const json = await uploadEvaluationVideo({
        blob,
        mimeType: blob.type,
        teId: meta.teId,
        tableNumber: meta.tableNumber,
        cameraPosition: side === "B" ? "B" : "A",
        clipKind,
        recordedAt: meta.startedAt ? new Date(meta.startedAt) : new Date(),
        viewSetup: {
          clip_kind: clipKind,
          source: clipKind,
          capture_id: meta.captureId,
          table_number: meta.tableNumber,
          camera_position: side === "B" ? "B" : "A",
          ...(share || {}),
          table_number:
            meta.tableNumber > 0 ? meta.tableNumber : share?.table_number,
        },
      });

      captureMetaRef.current = null;
      setActiveTeId(null);
      setUploading(false);
      return json;
    } catch (err) {
      discardRecorder();
      setUploading(false);
      setError(err?.message || "Table camera video upload failed");
      throw err;
    }
  }, [discardRecorder, stopTracksIfOwned]);

  useEffect(() => {
    if (!socket || !enabled) return undefined;

    const matchesTable = (payload) =>
      phoneCoversTable(payload?.table_number, {
        tableNumber: tableNumberRef.current,
        tableNumbers: tableNumbersRef.current,
      });

    const onStart = (payload = {}) => {
      if (!enabledRef.current) return;
      if (!resolveTableCameraSource(payload)) return;
      if (!matchesTable(payload)) return;
      startLocalRecording(payload).catch((err) => {
        console.error("useTableInstructionRecorder start failed:", err);
        setError(err?.message || "Failed to start table recording");
        discardRecorder();
      });
    };

    const onStop = (payload = {}) => {
      if (!resolveTableCameraSource(payload) && !recorderRef.current) return;
      if (payload?.table_number != null && !matchesTable(payload)) return;
      if (!recorderRef.current) return;
      stopAndUpload().catch((err) => {
        console.error("useTableInstructionRecorder stop/upload failed:", err);
      });
    };

    socket.on("tableVideoRecordingStart", onStart);
    socket.on("tableVideoRecordingStop", onStop);
    return () => {
      socket.off("tableVideoRecordingStart", onStart);
      socket.off("tableVideoRecordingStop", onStop);
    };
  }, [socket, enabled, startLocalRecording, stopAndUpload, discardRecorder]);

  // Tear down if table-phone mode is disabled mid-recording.
  useEffect(() => {
    if (!enabled && (recording || recorderRef.current)) {
      discardRecorder();
    }
  }, [enabled, recording, discardRecorder]);

  return {
    recording,
    uploading,
    error,
    activeTeId,
  };
}
